import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AudioWaveform, Mic, Cpu, ArrowLeft } from "lucide-react";
import { api, endpoints } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger
} from "@/components/ui/dialog";

function KindBadge({ kind }) {
  const map = {
    music:      { label: "MUSIC",      color: "#00E5FF" },
    talk:       { label: "TALK",       color: "#00BFFF" },
    news:       { label: "NEWS",       color: "#7FD8FF" },
    joke:       { label: "JOKE",       color: "#FFB84D" },
    weather:    { label: "WEATHER",    color: "#B8ECFF" },
    horoscope:  { label: "STARS",      color: "#C39BFF" },
    history:    { label: "STORIA",     color: "#FFB84D" },
    interview:  { label: "INTERVIEW",  color: "#00BFFF" },
    motivation: { label: "MOTIVATION", color: "#7FD8FF" },
    answer:     { label: "ANSWER",     color: "#00E5FF" },
    community:  { label: "COMMUNITY",  color: "#FF6B1A" },
    jingle:     { label: "JINGLE",     color: "#00E5FF" },
    appointment:{ label: "APPUNTAMENTO", color: "#FFB84D" },
    rubric:     { label: "RUBRICA",    color: "#C39BFF" },
    insert:     { label: "INSERTO",    color: "#7FD8FF" },
    future_pill:{ label: "PILLOLA",    color: "#B8ECFF" },
    past_pill:  { label: "PILLOLA",    color: "#B8ECFF" },
    generated:  { label: "GENERATO",   color: "#FF6B1A" },
    intro:      { label: "INTRO DJ",   color: "#00BFFF" },
  };
  const m = map[kind] || { label: (kind || "SEG").toUpperCase(), color: "#00BFFF" };
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded shrink-0"
      style={{ background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}55` }}>
      {m.label}
    </span>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function ReplayArchive() {
  const [segments, setSegments] = useState([]);
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);
  // v13.86 · archivio /api/replay (paginato, filtro data); se vuoto → fallback /api/segments come prima
  const [source, setSource] = useState("replay");
  const [date, setDate] = useState("");
  // v13.87 · raggruppamento programma → puntata
  const [program, setProgram] = useState("");
  const [episode, setEpisode] = useState(() => new URLSearchParams(window.location.search).get("episode") || "");
  const [programs, setPrograms] = useState({ programs: [], episodes: [] });
  useEffect(() => { api.get("/replay/programs").then((r) => setPrograms(r.data || { programs: [], episodes: [] })).catch(() => {}); }, []);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const [rep, djList] = await Promise.all([
          api.get("/replay", { params: { page, page_size: 30, ...(date ? { date } : {}), ...(program ? { program } : {}), ...(episode ? { episode_id: episode } : {}) } }).then(r => r.data).catch(() => null),
          endpoints.djs().catch(() => []),
        ]);
        if (!live) return;
        setDjs(djList || []);
        if (rep && (rep.total > 0 || date || program || episode)) {
          setSource("replay");
          setSegments(rep.items || []);
          setPages(rep.pages || 1);
          setTotal(rep.total || 0);
        } else {
          const segs = await endpoints.segments().catch(() => []);
          if (!live) return;
          setSource("segments");
          setSegments((segs || []).slice().reverse());
          setPages(1); setTotal((segs || []).length);
        }
      } catch (e) {}
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [page, date, program, episode]);

  const audioFor = (s) => {
    if (source === "replay" && s.audio_available === false) return null;
    let u = s.audio_url || s.speech_audio_url || s.music_audio_url || null;
    if (u && u.startsWith("/api/")) u = endpoints.audioUrl(u);
    return u;
  };

  return (
    <div className="relative min-h-screen text-white bg-black" data-testid="replay-page">
      <div aria-hidden className="fixed inset-0 pointer-events-none rai-bg" style={{ zIndex: 0 }}>
        <div className="rai-bg-grid" />
        <div className="rai-bg-orb rai-bg-orb-1" />
        <div className="rai-bg-orb rai-bg-orb-2" />
      </div>

      <header className="glass sticky top-0 z-40 relative">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="replay-back" className="btn-outline focusable !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
          <div className="label-mono flex items-center gap-2">
            <AudioWaveform className="w-3 h-3" /> RE-PLAY · ARCHIVIO STORICO
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        <h1 className="font-display font-black text-3xl sm:text-4xl mb-2 text-glow-blue" data-testid="replay-page-heading">
          RE-PLAY (Archivio Storico)
        </h1>
        <p className="text-xs font-mono text-zinc-400 mb-6 max-w-2xl">
          Riascolta i set dei DJ, i dischi del giorno e i momenti talk andati in onda.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="replay-filters">
          <label className="text-[11px] font-mono text-zinc-400 flex items-center gap-2">Programma
            <select value={program} onChange={(e) => { setProgram(e.target.value); setEpisode(""); setPage(1); }} className="bg-black/60 border border-cyan-500/30 rounded px-2 py-1 text-xs text-white" data-testid="replay-program">
              <option value="">tutti</option>
              {programs.programs.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          {(program || episode) && programs.episodes.filter((e) => !program || e.program === program).length > 0 && (
            <label className="text-[11px] font-mono text-zinc-400 flex items-center gap-2">Puntata
              <select value={episode} onChange={(e) => { setEpisode(e.target.value); setPage(1); }} className="bg-black/60 border border-cyan-500/30 rounded px-2 py-1 text-xs text-white" data-testid="replay-episode">
                <option value="">tutte</option>
                {programs.episodes.filter((e) => !program || e.program === program).map((e) => <option key={e.id} value={e.id}>{e.date} · {e.theme || e.program}</option>)}
              </select>
            </label>
          )}
          <label className="text-[11px] font-mono text-zinc-400 flex items-center gap-2">Giorno
            <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPage(1); }}
              className="bg-black/60 border border-cyan-500/30 rounded px-2 py-1 text-xs text-white" data-testid="replay-date" />
          </label>
          {date && (
            <button onClick={() => { setDate(""); setPage(1); }} className="text-[11px] font-mono text-cyan-400 hover:text-white" data-testid="replay-date-clear">✕ tutti i giorni</button>
          )}
          <span className="text-[11px] font-mono text-zinc-500 ml-auto" data-testid="replay-total">{total} element{total === 1 ? "o" : "i"}{source === "segments" ? " · copioni DJ" : ""}</span>
        </div>

        {loading && (
          <div className="console-panel p-6 text-zinc-500 text-sm font-mono">Caricamento archivio…</div>
        )}
        {!loading && segments.length === 0 && (
          <div className="console-panel p-6 text-zinc-500 text-sm font-mono">
            <span className="text-cyan-400">▸</span> Archivio vuoto.
          </div>
        )}

        <div className="space-y-2">
          {segments.map((s, idx) => {
            const dj = djs.find(d => d.name === s.dj_name);
            const url = audioFor(s);
            return (
              <Dialog key={`${s.id || "seg"}-${s.started_at || idx}`}>
                <DialogTrigger asChild>
                  <button
                    className="console-panel p-4 flex items-start gap-4 w-full text-left hover:border-cyan-400/60 transition"
                    data-testid={`replay-page-item-${s.id || idx}`}
                  >
                    <KindBadge kind={s.kind} />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold truncate">{s.title}</div>
                      <div className="text-xs font-mono text-zinc-500 mt-1 uppercase tracking-widest">
                        {s.started_at && <span className="text-zinc-400 mr-2">{fmtWhen(s.started_at)}</span>}
                        {(s.dj_name || s.artist) && <><Mic className="w-2.5 h-2.5 inline mr-1" /> {s.dj_name || s.artist}</>}
                        {s.llm_provider && <> · <Cpu className="w-2.5 h-2.5 inline mx-1" /> {s.llm_provider}</>}
                        {s.program && s.program !== s.title && <span className="ml-2 text-amber-200/80">· {s.program}</span>}
                        {url && <span className="ml-2 text-cyan-400">· ▶ RE-PLAY</span>}
                        {source === "replay" && s.audio_available === false && <span className="ml-2 text-zinc-600">{s.scaletta ? "· brano in scaletta" : "· audio non disponibile"}</span>}
                      </div>
                      {s.script && <p className="text-sm font-mono text-zinc-400 mt-2 line-clamp-2">{s.script}</p>}
                    </div>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl bg-[#02040A] text-white"
                  data-testid={`replay-page-dialog-${s.id || idx}`}
                  style={{ border: "1px solid rgba(0,191,255,0.4)", boxShadow: "0 0 40px rgba(0,191,255,0.25)" }}>
                  <DialogHeader>
                    <div className="flex items-center gap-4">
                      {dj?.avatar_url && (
                        <img src={dj.avatar_url} alt={dj.name} className="w-16 h-16 rounded-lg object-cover"
                          style={{ border: `1px solid ${dj.accent_color || "#00BFFF"}55`, boxShadow: `0 0 24px ${dj.accent_color || "#00BFFF"}66` }} />
                      )}
                      <div>
                        <DialogTitle className="text-2xl font-display font-black text-glow-blue" style={{ color: dj?.accent_color || "#00BFFF" }}>
                          {s.dj_name || s.artist || "DJ"}
                        </DialogTitle>
                        <DialogDescription className="text-xs font-mono uppercase tracking-widest text-cyan-500 mt-1">
                          {s.kind}{s.llm_provider ? ` · ${s.llm_provider}` : ""}{s.started_at ? ` · ${fmtWhen(s.started_at)}` : ""}
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                  <div className="mt-2">
                    <div className="text-sm font-semibold text-zinc-300 mb-2">{s.title}</div>
                    {url && (
                      <audio controls preload="none" src={url} className="w-full mb-3"
                        style={{ height: 34 }} data-testid={`replay-page-audio-${s.id || idx}`} />
                    )}
                    {source === "replay" && s.audio_available === false && (
                      <div className="text-xs font-mono text-zinc-500 mb-3">Audio non disponibile per questo passaggio{s.backfill ? " (voce storica ricostruita dal registro)" : ""}.</div>
                    )}
                    {(source === "segments" || s.script) && (
                      <div className="max-h-[50vh] overflow-y-auto rounded-lg bg-black/60 border border-cyan-500/20 p-4 text-sm font-mono text-zinc-300 leading-relaxed whitespace-pre-wrap">
                        {s.script || <span className="italic text-zinc-500">Nessun copione testuale disponibile per questo segmento.</span>}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            );
          })}
        </div>

        {source === "replay" && pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6" data-testid="replay-pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-outline !py-1.5 !px-3 !text-[11px] disabled:opacity-40" data-testid="replay-prev">◂ Prec</button>
            <span className="text-[11px] font-mono text-zinc-400" data-testid="replay-page">pagina {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="btn-outline !py-1.5 !px-3 !text-[11px] disabled:opacity-40" data-testid="replay-next">Succ ▸</button>
          </div>
        )}
      </section>
    </div>
  );
}
